import React from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as UserEditIcon } from './icons/user-edit.svg';
import { ReactComponent as TrashIcon } from './icons/trash.svg';
import { useLanguage } from './LanguageContext';
import { fetchMembers, deleteMember } from './api';
import Layout from './Layout';

const Home = () => {
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();
  const [members, setMembers] = React.useState([]);
  const [searchQuery, setSearchQuery] = React.useState('');

  React.useEffect(() => {
    const loadMembers = async () => {
      try {
        const data = await fetchMembers();
        console.log("Full API Response:", data);

        if (Array.isArray(data)) {
          const mappedMembers = data.map(user => ({
            id: user.user_id,
            name: user.name,
            memberId: user.member_id,
            subscriptionType: user.membership_type ? user.membership_type.charAt(0).toUpperCase() + user.membership_type.slice(1).toLowerCase() : 'Silver',
            status: user.status ? user.status.charAt(0).toUpperCase() + user.status.slice(1).toLowerCase() : 'Attivo',
            email: user.email,
            phone: user.phone,
            startDate: user.start_date,
            endDate: user.end_date
          }));
          setMembers(mappedMembers);
        } else {
          console.error("API response is not an array:", data);
        }
      } catch (error) {
        console.error("Failed to load members", error);
      }
    };

    loadMembers();
  }, []);

  const handleDelete = async (id) => {
    try {
      await deleteMember(id);
      setMembers(members.filter(member => member.id !== id));
    } catch (error) {
      console.error("Error deleting member:", error);
    }
  };

  const handleEdit = (member) => {
    navigate('/create', { state: { editMode: true, memberData: member } });
  };

  const filteredMembers = members.filter(member =>
    member.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const subscriptionKeyMap = {
    'Bronze': 'subscription_bronze',
    'Silver': 'subscription_silver',
    'Gold': 'subscription_gold',
    'Platinum': 'subscription_platinum',
  };

  const statusKeyMap = {
    'Attivo': 'status_attivo',
    'Inattivo': 'status_inattivo',
    'Scaduto': 'status_scaduto',
  };

  return (
    <Layout mainClassName="p-10 pt-24">
      {/* Small language selector (top-right) */}
      <div className="absolute right-6 top-6">
        <div className="w-auto bg-gradient-to-b from-white to-[#F0F2F5] rounded-full shadow-[0_8px_16px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_10px_20px_-4px_rgba(0,0,0,0.15)] transition-all">
          <select
            aria-label={t('languageLabel')}
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="text-sm bg-transparent px-4 py-2 rounded-full outline-none"
          >
            <option value="it">Italian</option>
            <option value="en">English</option>
          </select>
        </div>
      </div>

      <h1 className="text-3xl font-extrabold text-slate-800 mb-6 pl-7">{t('membersListTitle')}</h1>

      {/* Search Bar */}
      <div className="mb-6 px-7">
        <div className="relative flex items-center max-w-md">
          {/* Search Icon */}
          <span className="absolute left-4 text-[#64748B] pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
          </span>
          <input
            id="member-search"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('searchPlaceholder')}
            aria-label={t('searchPlaceholder')}
            className="w-full pl-11 pr-10 py-3 rounded-full bg-white border border-slate-200 shadow-sm text-sm text-slate-700 placeholder-slate-400 outline-none focus:ring-2 focus:ring-[#001621]/30 focus:border-[#001621]/50 transition-all font-poppins"
          />
          {/* Clear button */}
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
              className="absolute right-4 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Table Header */}
      <div className="grid grid-cols-12 bg-[#77818A] text-black font-poppins font-semibold text-sm py-6 px-6 rounded-2xl tracking-tight">
        <div className="col-span-2">{t('table_member')}</div>
        <div className="col-span-2">{t('table_id')}</div>
        <div className="col-span-2">{t('table_subscription')}</div>
        <div className="col-span-2">{t('table_status')}</div>
        <div className="col-span-2"></div>
      </div>


      {/* Member List */}
      <div>
        {filteredMembers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mb-3 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <p className="text-sm font-poppins">{t('searchNoResults')}</p>
          </div>
        ) : filteredMembers.map((member) => (
          <div
            key={member.id}
            className="grid grid-cols-12 gap-x-4 items-center py-4 px-4 text-sm text-black border-b border-black"
          >
            <div className="flex items-center gap-4 col-span-2">
              <span className="col-span-1 text-black font-medium">{member.name}</span>
            </div>
            <div className="col-span-2 text-black font-medium">{member.memberId}</div>
            <div className="col-span-2 text-black font-medium">{t(subscriptionKeyMap[member.subscriptionType] || member.subscriptionType)}</div>
            <div className="col-span-2 text-black font-medium">{t(statusKeyMap[member.status] || member.status)}</div>
            <div className="flex items-center justify-end gap-2 col-span-4">
              <button
                onClick={() => handleEdit(member)}
                aria-label={`Edit ${member.name}`}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-800 text-white hover:bg-slate-700 transition-colors"
              >
                <UserEditIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleDelete(member.id)}
                aria-label={`Delete ${member.name}`}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
};

export default Home;
